import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/lib/api";
import { Eye, EyeOff, Info } from "lucide-react";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import Brand from "@/components/common/Brand";

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreed) { setError(t("register.agree_error")); return; }
    setLoading(true);
    try {
      await register(email, password, newsletter);
      navigate("/onboarding/profile");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="register-page">
      <div className="h-14 bg-[#0A0A0A] flex items-center justify-between px-4">
        <button data-testid="register-back-btn" onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <Brand className="text-xl" />
        <LanguageSwitcher compact />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full">
        <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("register.title")}</h1>
        <p className="text-sm text-[#999] mt-1">{t("register.subtitle")}</p>

        {error && (
          <InfoCard variant="danger" data-testid="register-error" size="sm">{error}</InfoCard>
        )}

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("login.email")}</label>
            <input
              data-testid="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              placeholder={t("login.email_placeholder")}
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("login.password")}</label>
            <div className="relative mt-1">
              <input
                data-testid="register-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 pr-12 rounded-full border border-[#E5E5E5] bg-white text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder={t("register.password_placeholder")}
                required
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-3 text-[#999]">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="mt-1 text-[10px] text-[#BBB]">{t("register.password_hint")}</p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              data-testid="register-agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[#0A0A0A]"
            />
            <span className="text-xs text-[#666]">
              {t("register.agree")}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              data-testid="register-newsletter"
              type="checkbox"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
              className="mt-0.5 accent-[#0A0A0A]"
            />
            <span className="text-xs text-[#666]">
              {t("register.newsletter", "Ja, ich möchte den Newsletter mit Neuigkeiten und Angeboten erhalten. Abmeldung jederzeit möglich.")}
            </span>
          </label>
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? t("register.submitting") : t("register.submit")}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#999]">
          {t("register.has_account")}{" "}
          <Link to="/login" data-testid="login-link" className="text-[#0A0A0A] font-medium hover:underline">
            {t("register.login_link")}
          </Link>
        </p>
      </div>
    </div>
  );
}
